import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Users, Shield, Clock, MapPin, Car, TrendingUp, MessageCircle } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { apiService } from '../services/api';
import { useSettings } from '../contexts/SettingsContext';
import { siteImages } from '../data/siteImages';

const trustItems = [
  { icon: Car, label: 'Secure parking' },
  { icon: TrendingUp, label: 'Great foot traffic' },
  { icon: Building2, label: 'Affordable rentals' },
  { icon: MapPin, label: 'Prime location' }
];

const features = [
  { icon: Building2, title: 'Modern Retail Spaces', description: 'Flexible layouts for any business.' },
  { icon: Users, title: 'Thriving Community', description: "Join Chinhoyi's premier destination." },
  { icon: Shield, title: 'Secure & Safe', description: '24/7 security and modern systems.' },
  { icon: Clock, title: 'Flexible Terms', description: 'Competitive rates, tenant-friendly.' }
];

export default function Home() {
  const [stats, setStats] = useState({ availableSpaces: 0, floorLevels: 0, rating: 10 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const reduceMotion = useReducedMotion();
  const { businessInfo } = useSettings();
  const phone = businessInfo?.phone || '+263-71-708-2807';
  const whatsappUrl = `https://wa.me/${phone.replace(/\D/g, '')}`;

  useEffect(() => {
    loadHomeStats();
  }, []);

  const loadHomeStats = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getHomeStats();
      if (response.status === 'success' && response.data) {
        setStats({
          availableSpaces: response.data.availableSpaces || 0,
          floorLevels: response.data.floorLevels || 0,
          rating: response.data.rating || 10
        });
      }
    } catch (err) {
      console.error('Error loading home stats:', err);
      setError('Failed to load statistics');
      setStats({ availableSpaces: 50, floorLevels: 2, rating: 10 });
    } finally {
      setLoading(false);
    }
  };

  const statsData = [
    { number: `${stats.availableSpaces || 0}+`, label: 'Spaces' },
    { number: (stats.floorLevels || 0).toString(), label: 'Floors' },
    { number: '95%', label: 'Occupancy' },
    { number: (stats.rating || 10).toString(), label: 'Rating' }
  ];

  const anim = reduceMotion ? {} : { opacity: 0, y: 16 };
  const animEnd = reduceMotion ? {} : { opacity: 1, y: 0 };
  const t = reduceMotion ? { duration: 0 } : { duration: 0.4 };

  return (
    <div className="min-h-screen bg-surface">
      <Helmet>
        <title>The Gandi Mall – Chinhoyi&apos;s Shopping & Business Destination</title>
        <meta name="description" content="Premium retail and office spaces in Chinhoyi. Book a space, view shops, join our community." />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center bg-ink text-white overflow-hidden">
        <img
          src={siteImages.hero}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-75"
          onError={(e) => {
            if (!e.currentTarget.dataset.fb) {
              e.currentTarget.dataset.fb = '1';
              e.currentTarget.src = siteImages.heroFallback;
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/50 via-ink/40 to-ink/85" />
        <div className="relative max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-2xl">
            <motion.p
              initial={anim}
              animate={animEnd}
              transition={t}
              className="text-primary-light text-sm font-medium uppercase tracking-wider mb-4"
            >
              Chinhoyi · Retail & Business
            </motion.p>
            <motion.h1
              initial={anim}
              animate={animEnd}
              transition={{ ...t, delay: 0.05 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6"
            >
              Chinhoyi&apos;s  shopping & business destination
            </motion.h1>
            <motion.p
              initial={anim}
              animate={animEnd}
              transition={{ ...t, delay: 0.1 }}
              className="text-stone-300 text-lg sm:text-xl mb-8"
            >
              Premium retail and office spaces. Modern units, prime location, thriving community.
            </motion.p>
            <motion.div
              initial={anim}
              animate={animEnd}
              transition={{ ...t, delay: 0.15 }}
              className="flex flex-wrap gap-3"
            >
              <Link
                to="/shops"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-ink"
              >
                Book a Space <ArrowRight size={18} />
              </Link>
              <Link
                to="/shops"
                className="inline-flex items-center h-12 px-6 rounded-xl border border-stone-500 text-stone-200 font-medium hover:bg-white/5"
              >
                View Shops
              </Link>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#25D366] text-white font-medium hover:opacity-90"
              >
                <MessageCircle size={18} /> WhatsApp
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-12 lg:py-16 border-b border-stone-200 bg-surface-raised">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {trustItems.map((item, i) => (
              <motion.div
                key={item.label}
                initial={anim}
                whileInView={animEnd}
                viewport={{ once: true }}
                transition={{ ...t, delay: i * 0.05 }}
                className="flex flex-col items-center text-center gap-3"
              >
                <div className="w-12 h-12 rounded-2xl bg-primary-light text-primary flex items-center justify-center">
                  <item.icon size={24} />
                </div>
                <span className="text-ink text-sm font-medium">{item.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 lg:py-20 bg-surface">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-20 rounded-2xl bg-surface-muted animate-pulse" />
              ))}
            </div>
          ) : (
            <motion.div
              initial={anim}
              whileInView={animEnd}
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8"
            >
              {statsData.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-primary">{stat.number}</div>
                  <div className="text-ink-muted text-sm font-medium mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          )}
          {error && <p className="text-center text-ink-muted text-sm mt-4">{error}</p>}
        </div>
      </section>

      {/* Features */}
      <section className="py-16 lg:py-24 bg-surface-raised">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-ink mb-3">Why Gandi Mall?</h2>
            <p className="text-ink-muted">Space and support designed for success.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={anim}
                whileInView={animEnd}
                viewport={{ once: true }}
                transition={{ ...t, delay: i * 0.06 }}
                className="p-6 rounded-2xl bg-surface shadow-card hover:shadow-card-hover transition-shadow"
              >
                <div className="w-11 h-11 rounded-xl bg-primary-light text-primary flex items-center justify-center mb-4">
                  <f.icon size={22} />
                </div>
                <h3 className="font-semibold text-ink mb-1">{f.title}</h3>
                <p className="text-ink-muted text-sm">{f.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Visit */}
      <section className="py-16 lg:py-24 bg-surface">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={anim}
              whileInView={animEnd}
              viewport={{ once: true }}
              className="relative rounded-2xl overflow-hidden aspect-[4/3] lg:aspect-[3/2]"
            >
              <img
                src={siteImages.visitUs}
                alt="Visit Gandi Mall"
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  if (!e.currentTarget.dataset.fb) {
                    e.currentTarget.dataset.fb = '1';
                    e.currentTarget.src = siteImages.visitUsFallback;
                  }
                }}
              />
            </motion.div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-ink mb-4">Visit Us</h2>
              <p className="text-ink-muted text-lg mb-6">
                See why businesses choose Gandi Mall. Prime Chinhoyi location, secure parking, strong foot traffic.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/shops"
                  className="inline-flex h-11 px-5 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark"
                >
                  View Shops
                </Link>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border border-stone-300 text-ink font-medium hover:bg-surface-muted"
                >
                  <MessageCircle size={18} /> Directions
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-primary text-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Ready to grow your business?</h2>
          <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">
            Browse spaces and submit your application today.
          </p>
          <Link
            to="/shops"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white text-primary font-semibold hover:bg-stone-100"
          >
            Explore Shops <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
